/**
 * PromotionRegisterContainer
 * --
 */
import React from 'react';
import PromotionRegisterPresenter from './PromotionRegisterPresenter';
import MessageAlert from 'utils';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetProductCategories } from 'hooks/ProductCategoryHooks';
import { useGetProducts } from 'hooks/ProductHooks';
import dayjs from 'dayjs';
import { STORE_URL } from 'utils';
import {
  useCreateMapPromotion,
  useGetMapPromotions,
  useUpdatePromotion,
  promotionQueryKeys,
} from 'hooks/PromotionHooks';
import { useBulkUpdateProductPolicy } from 'hooks/ProductPolicyHooks';
import { useUpdateProductPolicy } from 'hooks/ProductPolicyHooks';
import { FileManager } from 'utils/FileManager';
import { useQueryClient } from '@tanstack/react-query';

const PROMOTION_INITIAL_VALUE = {
  promotion_id: null,
  promotion_title: '',
  promotion_description: '',
  promotion_start_date: dayjs().unix(),
  promotion_end_date: dayjs().unix(),
  promotion_content: '',
  promotion_code: '',
  examination_status: 'APPLY',
  is_show: 'Y',
  promotion_type: 'PROMOTION',
  thumbnail_url: '',
  is_main: 'N',
  order: 0,
};

const DISCOUNT_INITIAL_VALUE = {
  product_policy_id: null,
  policy_type: 'PROMOTION',
  commission_rate: 0,
  commission_type: 'R',
  discount_rate: 0,
  discount_type: 'R',
  reserve_rate: 0,
  reserve_type: 'R',
  share_ratio: 0,
  max_discount_price: 0,
  is_show: 'Y',
};
/**----------------------------------------
 * [Component] 총관리자: 프로모션 등록 > 컨테이너
 * ----------------------------------------
 */
const PromotionRegisterContainer = (props) => {
  const queryClient = useQueryClient();
  const {
    data = null,
    modalType = 'CREATE',
    onOpen = () => false,
    isOpened = false,
    currentShop = null,
  } = props;
  /* ===== ROUTE ===== */
  /* ===== STATE ===== */
  const [, setIsLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(PROMOTION_INITIAL_VALUE);
  const [discount, setDiscount] = React.useState(DISCOUNT_INITIAL_VALUE);
  const [file, setFile] = React.useState(null);
  const [duplicateProductList, setDuplicateProductList] = React.useState(null);
  const [isDuplicateOpened, setIsDuplicateOpened] = React.useState(false);
  const [selectedList, setSelectedList] = React.useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);

  /* ===== MUTATE ===== */
  const { createMapPromotion } = useCreateMapPromotion();
  const { updatePromotion } = useUpdatePromotion();
  const { updateProductPolicy } = useUpdateProductPolicy();
  const { bulkUpdateProductPolicy } = useBulkUpdateProductPolicy();

  const mutate = {
    CREATE: createMapPromotion,
    UPDATE: {
      PROMOTION: updatePromotion,
      PRODUCT_POLICY: updateProductPolicy,
      MAP_PRODUCT_POLICY: bulkUpdateProductPolicy,
    },
  };
  /* ===== Data ===== */
  // NOTE: 입점업체 목록 데이터 호출
  const { data: Shops } = useGetShopInformations(null, {
    initialData: [],
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const filtered = data.filter((_item) => _item.type !== 'STORE');

      return filtered;
    },
  });
  // NOTE: 상품 카테고리 데이터 호출
  const { data: Categories } = useGetProductCategories(
    { type: 'PRODUCT' },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );
  // NOTE: 상품 데이터 호출
  const { data: Products } = useGetProducts(
    {
      join_tables: '["product_file"]',
      type: 'DELIVERY',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );
  const { data: Promotions } = useGetMapPromotions(
    {
      type: 'PRODUCT',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );

  /* ===== VARIABLE ===== */

  const newProductList = React.useMemo(() => {
    return Products?.map((product) => {
      const { product_file = [] } = product;
      const thumbnail =
        product_file.find((file) => file.type === 'THUMBNAIL') ?? null;
      const matchedCategory = Categories?.find(
        (category) =>
          category.product_category_id === product.product_category_id
      );
      const matchedShop = Shops?.find(
        (shop) => shop.shop_information_id === product.shop_information_id
      );
      return {
        key: product.product_id,
        ...product,
        product_category_name: matchedCategory?.product_category_name,
        shop_information_name: matchedShop?.shop_information_name,
        thumbnail: thumbnail ? `${STORE_URL}/${thumbnail.file_url}` : thumbnail,
      };
    });
  }, [Shops, Categories, Products]);
  /* ===== FUNCTION ===== */
  const onChange = (key, value, callback = setInputValue) =>
    callback((prev) => ({
      ...prev,
      [key]: value,
    }));
  /**
   * 인풋값 벨류 체크
   * ---
   * @returns
   */
  const handleValueCheck = () => {
    const {
      promotion_title,
      promotion_description,
      promotion_content,
      promotion_start_date,
      promotion_end_date,
    } = inputValue;
    if (
      !promotion_title ||
      !promotion_description ||
      !promotion_content ||
      !promotion_start_date ||
      !promotion_end_date
    ) {
      MessageAlert.info('필수 입력 값을 모두 입력해주세요.');
      return false;
    }

    // if(!file){
    //   MessageAlert.info('썸네일을 등록해주세요.');
    // return false;
    // }
    return true;
  };
  /**
   * 스태이트 초기화
   * ---
   */
  const handleInitState = () => {
    setInputValue(PROMOTION_INITIAL_VALUE);
    setDiscount(DISCOUNT_INITIAL_VALUE);
    setSelectedList([]);
    setSearchText('');
    setSearchedColumn('');
    setCurrentPage(1);
    setDuplicateProductList([]);
    setIsDuplicateOpened(false);
    setFile(null);
    onOpen(false);
  };
  /**
   * 중복되는 상품 체크
   * ---
   */
  const handleDuplicationProductCheck = () => {
    if (!handleValueCheck()) return;

    const { promotion_start_date, promotion_end_date, promotion_id } =
      inputValue;

    const filteredPromotions = Promotions.filter((promotion) => {
      //동일 프로모션 제외
      if (promotion.promotion_id === promotion_id) return false;
      //아닐때만
      const start = dayjs.unix(promotion.promotion_start_date);
      const end = dayjs.unix(promotion.promotion_end_date);
      const inputStart = dayjs.unix(promotion_start_date);
      const inputEnd = dayjs.unix(promotion_end_date);
      //정책 활성화 여부
      const is_policy_enable =
        promotion.product_policy_is_show === 'Y' ? true : false;
      return (
        is_policy_enable &&
        (start.isBefore(inputEnd) || start.isSame(inputEnd)) &&
        (end.isAfter(inputStart) || end.isSame(inputStart))
      );
    });
    // console.log('filteredPromotions', filteredPromotions);
    // selectedList 순회하면서 유효한 프로모션에 포함된 상품인지 확인
    const duplicateProducts = selectedList
      .map((selected) => {
        const { product_id } = selected;

        // 해당 product_id를 포함하는 유효한 프로모션 찾기
        const matchedPromotion = filteredPromotions.find((promotion) => {
          const mapList = promotion?.map_product_policy ?? [];
          //아이디 일치하고, 맵핑된(POSSIBLE) 상태인것
          return mapList.some(
            (map) => map.product_id === product_id && map.is_enable === 'Y'
          );
        });

        if (matchedPromotion) {
          return {
            ...selected,
            promotion_title: matchedPromotion.promotion_title,
            promotion_end_date: matchedPromotion.promotion_end_date,
          };
        }

        return null;
      })
      .filter(Boolean); // null 제거
    setDuplicateProductList(duplicateProducts);
    console.log('duplicateProducts', duplicateProducts);
    setIsDuplicateOpened(true);
  };

  /**
   * 저장
   * --
   */
  const handleSubmit = async (t) => {
    const type = t.toLocaleUpperCase();
    const confirm = (msg = '') => window.confirm(msg);
    const message = type === 'CREATE' ? '등록' : '수정';
    if (
      !confirm(
        `기간내 중복된 상품은 제거 후 ${message}됩니다. ${message}하시겠습니까?`
      )
    )
      return;
    let success = false;

    try {
      setIsLoading(true);
      let data = null;
      let product_policy = null;
      //NOTE: 중복 상품 제거
      let notDuplicateSelectList = selectedList?.filter(
        (product) =>
          !duplicateProductList.some(
            (dupProduct) => dupProduct.product_id === product.product_id
          )
      );
      let statusCode = 500;
      //NOTE: 사진
      if (file) {
        const upload = await FileManager.upload('promotion', file);

        if (upload.status !== 200) {
          MessageAlert.info('파일 업로드에 실패하였습니다.');
          return;
        }
        inputValue.thumbnail_url = upload?.data?.file_name;
      }

      //NOTE: 생성
      if (type === 'CREATE') {
        data = {
          promotion: inputValue,
          product_policy: discount,
          product_ids: notDuplicateSelectList?.map((product) => ({
            product_id: product?.product_id,
          })),
        };
      }
      //NOTE: 수정
      if (type === 'UPDATE') {
        data = {
          ...inputValue,
          promotion_id: inputValue.promotion_id,
          map_product_policy: null,
          product_policy_id: null,
        };
        product_policy = discount;
        // 2. 기존 정책에서 중복된 애들만 'N' 처리
        const updatedPolicy = inputValue?.map_product_policy?.map((map) => {
          // 조건 1: map_product_policy에 있고, notDuplicateSelectList에 있는 경우 변화 없음
          const isInNotDuplicateSelectList = notDuplicateSelectList.some(
            (select) => select.product_id === map.product_id
          );
          // 조건 2: (2025.10.13 추가 - willy) isInNotDuplicateSelectList가 ture이면서 is_enable = 'N'인 상품은 Y로
          // 조건 2: map_product_policy에 있고, notDuplicateSelectList에 없는 경우 is_enable = 'N'
          return {
            ...map,
            is_enable: isInNotDuplicateSelectList ? map.is_enable === "Y" ? map.is_enable : 'Y' : "N", // 기존 정책에서 없으면 'N'
          };
        });

        // 3. selectedList 내에서 중복 제거 (예: [141, 141] 같은 거 필터링)
        const productCountMap = notDuplicateSelectList.reduce((acc, item) => {
          acc[item.product_id] = (acc[item.product_id] || 0) + 1;
          return acc;
        }, {});

        // 4. 새로운 매핑 추가 (조건 3: map_product_policy에 없고 notDuplicateSelectList에 있는 상품을 추가)
        const newMappings = notDuplicateSelectList
          .filter((item) => productCountMap[item.product_id] === 1) // 중복된 제품 제외
          .map((select) => ({
            product_id: select.product_id,
            product_policy_id: inputValue.product_policy_id,
            policy_status: 'POSSIBLE',
            is_enable: 'Y', // 새로운 선택된 상품은 'Y'
          }));

        // 5. 기존 정책과 새로운 매핑을 합친 결과
        // updatedPolicy와 newMappings를 합칠 때 중복된 product_id를 제거
        const allMappings = [...updatedPolicy, ...newMappings];

        // product_id 기준으로 중복 제거
        const uniqueMappings = allMappings.reduce((acc, item) => {
          if (
            !acc.some(
              (existingItem) => existingItem.product_id === item.product_id
            )
          ) {
            acc.push(item);
          }
          return acc;
        }, []);

        // 최종적으로 중복되지 않는 `notDuplicateSelectList`
        notDuplicateSelectList = uniqueMappings;
      }

      if (type === 'UPDATE') {
        //NOTE: 프로모션
        const { status } = await mutate[type].PROMOTION(data);
        if (status === 200) {
          //NOTE: 정책
          const { status: productPolicyStatus } = await mutate[
            type
          ].PRODUCT_POLICY(product_policy);
          //NOTE: 맵핑
          if (productPolicyStatus === 200) {
            const { status: mapProductStatus } = await mutate[
              type
            ].MAP_PRODUCT_POLICY(notDuplicateSelectList);
            statusCode = mapProductStatus;
          }
        }
      } else {
        const { status } = await mutate[type](data);
        statusCode = status;
      }
      if (statusCode === 200) {
        MessageAlert.success(`성공적으로 프로모션이 ${message}되었습니다.`);
        success = true;
      } else {
        MessageAlert.warning(`프로모션을 ${message}하는데 실패하였습니다.`);
      }
    } catch (err) {
      console.error(`[${message} PROMOTION] ERROR : `, err);
      MessageAlert.error(`프로모션을 ${message}하는데 오류가 발생하였습니다.`);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (success) {
          const queryKey = promotionQueryKeys.getPromotionsMapping({
            ...(currentShop && { shop_information_id: currentShop }),
            type: 'PRODUCT',
          });
          queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
          handleInitState();
        }
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 초기값 설정
   * ---
   */
  React.useEffect(() => {
    //모달
    if (isOpened && data && modalType === 'UPDATE') {
      const {
        promotion_id,
        promotion_title,
        promotion_description,
        promotion_start_date,
        promotion_end_date,
        promotion_content,
        examination_status,
        is_show,
        promotion_type,
        thumbnail_url,
        is_main,
        order,
        product_policy_id,
        map_product_policy = [],

        //정책
        policy_type,
        commission_rate,
        commission_type,
        discount_rate,
        discount_type,
        reserve_rate,
        reserve_type,
        share_ratio,
        max_discount_price,
        product_policy_is_show,
      } = data;
      const products = newProductList?.filter((product) =>
        map_product_policy.some(
          (map_product) =>
            map_product.product_id === product.product_id &&
            map_product.is_enable === 'Y'
        )
      );
      setInputValue({
        promotion_id,
        product_policy_id: product_policy_id,
        promotion_title,
        promotion_description,
        promotion_start_date,
        promotion_end_date,
        promotion_content,
        examination_status,
        is_show,
        promotion_type,
        thumbnail_url,
        is_main,
        order,
        map_product_policy,
      });
      setDiscount({
        product_policy_id: product_policy_id,
        policy_type,
        commission_rate,
        commission_type,
        discount_rate,
        discount_type,
        reserve_rate,
        reserve_type,
        share_ratio,
        max_discount_price,
        is_show: product_policy_is_show,
      });
      setSelectedList(products);
      setCurrentPage(1);
      setSearchText('');
      setSearchedColumn('');
      setFile(null);
    } else {
      //생성
      setInputValue(PROMOTION_INITIAL_VALUE);
      setDiscount(DISCOUNT_INITIAL_VALUE);
    }
  }, [data, isOpened, newProductList]);
  /* ===== RENDER ===== */
  return (
    <>
      <PromotionRegisterPresenter
        {...{
          PROMOTION_INITIAL_VALUE,
          DISCOUNT_INITIAL_VALUE,
          data: null,
          inputValue,
          onInputValue: setInputValue,
          onDiscount: setDiscount,
          onSubmit: handleSubmit,
          onChange,
          discount,
          setDiscount,

          /* Default data */
          productList: newProductList,
          categoryList: Categories,
          shopInformationList: Shops,
          /* New data */
          selectedList,
          onSelect: setSelectedList,
          // selectedProductList,
          // selectedCategoryList,
          // selectedShopInformationList,
          // setSelectedProductList,
          // setSelectedCategoryList,
          // setSelectedShopInformationList,
          file,
          onFileChange: setFile,
          /* */
          modalType,
          onOpen,
          currentPage,
          setCurrentPage,
          searchText,
          setSearchText,
          searchedColumn,
          setSearchedColumn,
          //중복상품 체크 모달 및 함수
          isDuplicateOpened,
          onDuplicateOpen: setIsDuplicateOpened,
          onDuplicateProductCheck: handleDuplicationProductCheck,
          //중복 상품 리스트
          duplicateProductList,
          onDuplicateSelect: setDuplicateProductList,
        }}
      />
    </>
  );
};
export default PromotionRegisterContainer;
